// Public, unguarded page shown when the backend cleared the session
// cookie but could not coordinate with Microsoft Entra (e.g. missing
// ENTRA_POST_LOGOUT_REDIRECT_URI, Entra 5xx). The user is effectively
// signed out of this app but may still hold an Entra session.
//
// This route sits outside RequireAdmin on purpose — if it lived under
// /admin/* the auth guard would bounce users straight to /auth/login
// and swallow the error. The route path also lives outside /auth/*
// because /auth is claimed by the Vite dev proxy and Encore's public
// API surface (apps/frontend/vite.config.ts).
//
// The recovery CTA uses a plain <a href> — NOT react-router's <Link>
// — because /auth/login is a backend endpoint that kicks off the
// Entra OIDC redirect (see apps/frontend/src/app/require-admin.tsx
// for the same pattern). A client-side Link would route inside the
// SPA and land on the catch-all NotFoundPage.

import { BrandHeader } from '../ui/brand-header'
import { buildLoginUrl } from '../hooks/api'

export function LogoutErrorPage() {
  return (
    <main
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-8 bg-surface p-6"
    >
      <BrandHeader variant="splash" />

      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-semibold text-ink">
          We couldn&apos;t sign you out cleanly
        </h1>
        <p className="text-sm text-ink/70">
          Your local session has been cleared, but we weren&apos;t able to
          confirm the sign-out with Microsoft Entra. If you share this
          device, Entra may still consider you signed in. Close this
          browser tab or sign out of Microsoft directly to ensure your
          account is fully signed out.
        </p>
      </div>

      <div className="flex flex-col items-center gap-3 sm:flex-row">
        {/* Primary: plain <a> so the browser performs a real navigation to
            the backend /auth/login endpoint (not a SPA route). */}
        <a
          href={buildLoginUrl('/')}
          className="inline-flex min-h-11 min-w-44 items-center justify-center rounded-md bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          Try sign-in again
        </a>

        {/* Secondary: react-router Link would work here since / is an SPA
            route, but we use a plain <a> for consistency and simplicity. */}
        <a
          href="/"
          className="inline-flex min-h-11 min-w-44 items-center justify-center rounded-md border border-ink/20 px-6 py-2.5 text-sm font-medium text-ink hover:bg-ink/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
        >
          Go home
        </a>
      </div>
    </main>
  )
}
